import { Loader2, Users } from 'lucide-react';

import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@design-system';

import { ShareRecipientPicker } from './share-recipient-picker';
import {
  getRecipientDisplayName,
  getRecipientOrgName,
  useShareClientDialog,
} from './use-share-client-dialog';

type Step = 'select' | 'acknowledge';

interface CollaborateOnCaseDialogProps {
  client: Client;
  partner?: Client | null;
  open: boolean;
  onClose: () => void;
}

function formatList(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;

  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

function recipientWithOrg(rel: SupervisorAdvisorRelationship): string {
  const orgName = getRecipientOrgName(rel);

  return orgName ? `${getRecipientDisplayName(rel)} at ${orgName}` : getRecipientDisplayName(rel);
}

// The Edward Jones per-case flow. The advisor picks who they collaborate with on this case, then
// acknowledges that Waterlily shares the case data with them to support that collaboration. There is
// no note step and no legacy attestation — EDJ advisors never share pre-cutoff clients.
export function CollaborateOnCaseDialog({
  client,
  partner,
  open,
  onClose,
}: CollaborateOnCaseDialogProps) {
  const dialog = useShareClientDialog<Step>({
    client,
    partner,
    open,
    onClose,
    initialStep: 'select',
    successToast: ({ recipientCount, clientCount }) => ({
      title: 'Collaboration started',
      description: `Now collaborating with ${recipientCount} ${
        recipientCount === 1 ? 'partner' : 'partners'
      } on ${clientCount} ${clientCount === 1 ? 'case' : 'cases'}.`,
    }),
    errorToastTitle: 'Failed to start collaboration',
  });

  const caseNames = [
    dialog.clientDisplayName,
    ...(dialog.shouldIncludePartner && dialog.partnerDisplayName
      ? [dialog.partnerDisplayName]
      : []),
  ];
  const recipientNames = dialog.selectedRecipients.map(recipientWithOrg);

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => !isOpen && dialog.handleClose()}
    >
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-primary-dark">Collaborate on this case</DialogTitle>
          <DialogDescription className="text-foreground">
            {dialog.step === 'select'
              ? `Who do you want to collaborate with on ${dialog.clientDisplayName}’s case?`
              : 'Review and acknowledge'}
          </DialogDescription>
        </DialogHeader>

        {dialog.step === 'select' && (
          <>
            <div className="py-2">
              <div className="mb-4 flex items-start gap-3">
                <div className="bg-primary/5 flex size-12 shrink-0 items-center justify-center rounded-full">
                  <Users className="text-primary-dark h-6 w-6" />
                </div>
                <p className="text-foreground flex-1 text-sm">
                  Selected partners will be able to view this client’s data in accordance with the
                  consent your client agreed to with Waterlily.
                </p>
              </div>

              {dialog.hasPartner && dialog.partnerDisplayName && (
                <label
                  htmlFor="collaborate-include-partner"
                  className="bg-muted/50 hover:bg-muted mb-3 flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors"
                >
                  <Checkbox
                    id="collaborate-include-partner"
                    checked={dialog.includePartner}
                    onCheckedChange={checked => dialog.setIncludePartner(checked === true)}
                    className="mt-0.5"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-950">
                      Also collaborate on {dialog.partnerDisplayName}’s case
                    </p>
                    <p className="text-foreground text-xs">
                      The same partners will be able to view their case too.
                    </p>
                  </div>
                </label>
              )}

              <ShareRecipientPicker
                recipients={dialog.filteredRecipients}
                selectedIds={dialog.selectedIds}
                alreadySharedIds={dialog.alreadySharedIds}
                onToggle={dialog.toggleRecipient}
                search={dialog.search}
                onSearchChange={dialog.setSearch}
                isLoading={dialog.isLoadingRecipients}
                hasNoRecipients={dialog.hasNoRecipients}
                loadingLabel="Loading partners…"
                noRecipientsLabel="No partner contacts are available right now. If you've already connected distribution partners, try closing and reopening this dialog."
                searchPlaceholder="Search by name, email, or organization…"
                noMatchesLabel="No partners match"
              />

              {dialog.selectedIds.length > 0 && (
                <p className="text-foreground mt-2 text-xs">{dialog.selectedIds.length} selected</p>
              )}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={dialog.handleClose}
              >
                Cancel
              </Button>
              <Button
                onClick={() => dialog.setStep('acknowledge')}
                disabled={dialog.selectedIds.length === 0}
              >
                Continue
              </Button>
            </DialogFooter>
          </>
        )}

        {dialog.step === 'acknowledge' && (
          <>
            <div className="py-2">
              <h3 className="mb-1 text-base font-semibold text-gray-950">
                How Waterlily supports this collaboration
              </h3>
              <p className="text-foreground text-sm">
                To help you collaborate with {formatList(recipientNames)} on{' '}
                {formatList(caseNames.map(name => `${name}’s`))}{' '}
                {caseNames.length === 1 ? 'case' : 'cases'}, Waterlily will share{' '}
                {caseNames.length === 1 ? 'their' : 'both clients’'} data with them, in accordance
                with the consent your client
                {caseNames.length === 1 ? '' : 's'} agreed to with Waterlily.
              </p>

              <ul className="mt-4 divide-y rounded-md border">
                {dialog.selectedRecipients.map(rel => (
                  <li
                    key={rel.advisorId}
                    className="p-3"
                  >
                    <p className="truncate text-sm font-medium text-gray-950">
                      {getRecipientDisplayName(rel)}
                    </p>
                    <p className="text-foreground truncate text-xs">{rel.advisorEmail}</p>
                    {getRecipientOrgName(rel) && (
                      <p className="text-muted-foreground truncate text-xs">
                        {getRecipientOrgName(rel)}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => dialog.setStep('select')}
                disabled={dialog.isPending}
              >
                Back
              </Button>
              <Button
                onClick={dialog.share}
                disabled={dialog.isPending}
              >
                {dialog.isPending ? (
                  <>
                    <Loader2 className="mr-1 size-4 animate-spin" />
                    Acknowledging…
                  </>
                ) : (
                  'Acknowledge'
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
